import { Dialog } from "@constellation-academy/epic-ui";
import { useFormik } from "formik";
import { Button } from "primereact/button";
import { OrderList } from "primereact/orderlist";
import React, { useState } from "react";
import { graphql, useFragment, useMutation } from "react-relay";
import { toast } from "react-toastify";
import styled from "styled-components";
import { v4 } from "uuid";
import * as Yup from "yup";
import { useTranslation } from "@i18n/hooks";
import { PodcastWithTimestampElementForm_EditPodcastWithTimestampElementMutation } from "@relay/PodcastWithTimestampElementForm_EditPodcastWithTimestampElementMutation.graphql";
import { PodcastWithTimestampElementForm_PodcastWithTimestampElementFragment$key } from "@relay/PodcastWithTimestampElementForm_PodcastWithTimestampElementFragment.graphql";
import {
	PodcastWithTimestampElementFormProps,
	PodcastWithTimestampElementFormState,
} from "./podcast-with-timestamp-element-form.interface";
import { DefaultTextFieldComponent } from "../../../../components/DefaultTextInput";
import { RenderConfig, ValidatedField } from "../../../../components/ValidatedField";
import { AudioFileTypeOptions } from "../../../../features/files/file-filters/file-filters.const";
import {
	OfferFileSelectionField as FileSelectionField,
	type FileV2,
} from "../../../../features/offer-files/offer-file-selection-field";
import { useHasPermissions } from "../../../../hooks/use-has-permissions";
import {
	addEditedFormToEditedFormsArray,
	resetArrayOfEditedForms,
} from "../../../../store/slices/CoreSlice";
import { useTypedDispatch } from "../../../../store/store.redux";
import { secondToTimestamp, timestampToSeconds } from "../../../../util/TimestampConverter";
import { CreateTimestampWithImageButton } from "../create-timestamp-with-image-button";
import { TimestampWithImageForm } from "../timestamp-with-image-form";
import { TimestampWithImage } from "../timestamp-with-image-form/timestamp-with-image-form.interface";
import { TimestampWithImageTemplate } from "../timestamp-with-image-template";
import { useDraftStatusUpdater } from "../use-draft-status-updater";

const PODCAST_WITH_TIMESTAMP_FRAGMENT = graphql`
	fragment PodcastWithTimestampElementForm_PodcastWithTimestampElementFragment on PodcastWithTimestamp {
		id
		title
		podcastFile {
			id
			name
		}
		timestampsWithImage {
			start
			end
			imageFile {
				id
				name
			}
		}
	}
`;

const EDIT_PODCAST_WITH_TIMESTAMP_ELEMENT_MUTATION = graphql`
	mutation PodcastWithTimestampElementForm_EditPodcastWithTimestampElementMutation(
		$input: EditPodcastWithTimestampInput!
	) {
		Admin {
			ElearningV2 {
				editPodcastWithTimestampElement(input: $input) {
					podcastWithTimestampElement {
						...PodcastWithTimestampElementForm_PodcastWithTimestampElementFragment
					}
				}
			}
		}
	}
`;

export const PodcastWithTimestampElementForm = ({
	podcastWithTimestampElementFragmentRef,
	onBack,
}: PodcastWithTimestampElementFormProps) => {
	const { t } = useTranslation(["common", "validation"]);
	const setDraft = useDraftStatusUpdater();
	const podcastWithTimestampElement =
		useFragment<PodcastWithTimestampElementForm_PodcastWithTimestampElementFragment$key>(
			PODCAST_WITH_TIMESTAMP_FRAGMENT,
			podcastWithTimestampElementFragmentRef,
		);

	const [editPodcastWithTimestampElement, isEditingPodcastWithTimestampElement] =
		useMutation<PodcastWithTimestampElementForm_EditPodcastWithTimestampElementMutation>(
			EDIT_PODCAST_WITH_TIMESTAMP_ELEMENT_MUTATION,
		);

	const formId = "PodcastWithTimestampElementFormComponent";

	const [editTimestamp, setIsEditingTimestamp] = useState<TimestampWithImage | null>(null);

	const dispatch = useTypedDispatch();

	const formik = useFormik<PodcastWithTimestampElementFormState>({
		initialValues: {
			id: podcastWithTimestampElement.id,
			title: podcastWithTimestampElement.title || "",
			podcastFile: podcastWithTimestampElement.podcastFile as FileV2,
			timestampsWithImage: podcastWithTimestampElement.timestampsWithImage
				? podcastWithTimestampElement.timestampsWithImage.map((timestampWithImage) => ({
						tempId: v4(),
						start: secondToTimestamp(timestampWithImage.start),
						end: secondToTimestamp(timestampWithImage.end),
						imageFileId: timestampWithImage.imageFile?.id,
						imageFile: timestampWithImage.imageFile as FileV2,
				  }))
				: [],
		},
		validationSchema: Yup.object().shape({
			title: Yup.string()
				.max(500, t("validation:titleMax", { max: 500 }))
				.required(t("validation:titleFieldRequired")),
			podcastFile: Yup.object().required(t("validation:podcastFileRequired")),
			timestampsWithImage: Yup.array().min(1).required(t("validation:atLeastOneTimestamp")),
		}),
		onSubmit: (values, { setSubmitting }) => {
			editPodcastWithTimestampElement({
				variables: {
					input: {
						elementId: values.id,
						newTitle: values.title,
						newPodcastFileId: values.podcastFile.id,
						newTimestampsWithImage: values.timestampsWithImage.map(
							(timestampWithImage) => ({
								start: timestampToSeconds(timestampWithImage.start),
								end: timestampToSeconds(timestampWithImage.end),
								imageFileId: timestampWithImage.imageFile?.id,
							}),
						),
					},
				},
				onCompleted: () => {
					dispatch(resetArrayOfEditedForms());
					toast.success(t("messages.elementSaved"));
				},
				updater: setDraft,
			});
			setSubmitting(false);
		},
	});

	const canUpload = useHasPermissions(["UserInAccountPermission_Nodes_UpdateNodes"]);

	const canDelete = useHasPermissions("onlyOwnerOfRoot");

	return (
		<form onSubmit={formik.handleSubmit} className="p-fluid">
			<ValidatedField<PodcastWithTimestampElementFormState, string>
				name={"title"}
				label={t("general.title")}
				onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
				component={({ fieldValue, updateField, fieldName, isValid }) => {
					return (
						<DefaultTextFieldComponent
							fieldName={fieldName + "-string"}
							disabled={isEditingPodcastWithTimestampElement}
							isValid={isValid}
							fieldValue={fieldValue}
							required
							updateField={(value) => {
								updateField(value);
								dispatch(addEditedFormToEditedFormsArray({ form: formId }));
							}}
						/>
					);
				}}
				formikConfig={formik}
			/>

			<ValidatedField<PodcastWithTimestampElementFormState, FileV2>
				name={"podcastFile"}
				label={t("forms.podcastFile")}
				onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
				component={({ fieldName, fieldValue, updateField, onChange }) => {
					return (
						<FileSelectionField
							name={fieldName}
							filterByFileTypes={AudioFileTypeOptions.map((a) => a.value)}
							selectedFile={fieldValue}
							setSelectedFile={updateField}
							onChange={onChange}
							canUploadFiles={canUpload}
							canDeleteFiles={canDelete}
						/>
					);
				}}
				formikConfig={formik}
			/>

			<ValidatedField<PodcastWithTimestampElementFormState, TimestampWithImage[]>
				name={"timestampsWithImage"}
				label={t("forms.timestamps")}
				onChange={() => dispatch(addEditedFormToEditedFormsArray({ form: formId }))}
				component={({ fieldValue, updateField }: RenderConfig<TimestampWithImage[]>) => {
					return (
						<>
							<div className="p-buttonset mb-2">
								<CreateTimestampWithImageButton
									fieldValue={fieldValue}
									updateField={updateField}
								/>
							</div>
							<OrderListWithoutControls
								className={"mt-2"}
								value={fieldValue || []}
								dragdrop
								listStyle={{ height: "auto" }}
								itemTemplate={(timestampWithImage: TimestampWithImage) => (
									<TimestampWithImageTemplate
										edit={() => setIsEditingTimestamp(timestampWithImage)}
										remove={() =>
											fieldValue &&
											updateField([
												...fieldValue.filter(
													(twi) =>
														twi.tempId != timestampWithImage.tempId,
												),
											])
										}
										timestampWithImage={timestampWithImage}
									/>
								)}
							/>
							{editTimestamp && (
								<Dialog visible={true} onHide={() => setIsEditingTimestamp(null)}>
									<TimestampWithImageForm
										timestampWithImage={editTimestamp}
										onSave={(updatedTimestampWithImage: TimestampWithImage) => {
											fieldValue &&
												updateField([
													...fieldValue.map((twi) =>
														twi.tempId ==
														updatedTimestampWithImage.tempId
															? updatedTimestampWithImage
															: twi,
													),
												]);
											setIsEditingTimestamp(null);
										}}
										onBack={() => setIsEditingTimestamp(null)}
									/>
								</Dialog>
							)}
						</>
					);
				}}
				formikConfig={formik}
			/>

			<Button
				disabled={isEditingPodcastWithTimestampElement}
				type="submit"
				label={t("actions.save")}
				className="mt-2"
			/>

			<Button
				type="button"
				onClick={onBack}
				label={t("actions.back")}
				className="p-button-secondary mt-2"
			/>
		</form>
	);
};

const OrderListWithoutControls = styled(OrderList)`
	.p-orderlist-controls {
		display: none;
	}

	.p-orderlist-list {
		.p-disabled {
			opacity: 1;
		}
	}
`;
